import React from 'react';
import { Button, Chip, FormHelperText, IconButton, Typography } from '@material-ui/core';
import { GetApp, Close } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

interface DocumentFileInputProps {
  label: string;
  name: string;
  value: File | null;
  setFieldValue: (field: string, value: File | null) => void;
  isSubmitting: boolean;
  touched?: boolean;
  errors?: string;
  accept?: string;
  existingUrl?: string | null;
  existingFileLabel?: string;
  onRemoveExisting?: () => void;
  disabled?: boolean;
  maxCsvPreviewLines?: number;
  maxSizeMb?: number;
}

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];

function getFileNameFromUrl(url: string): string {
  try {
    const clean = url.split('?')[0];
    return decodeURIComponent(clean.split('/').pop() ?? '');
  } catch {
    return url.split('/').pop() ?? '';
  }
}

function getFileExtension(name: string): string {
  const match = name.split('.').pop();
  return match ? match.toLowerCase() : '';
}

function isCsvFile(file: File): boolean {
  return file.type === 'text/csv' || /\.csv$/i.test(file.name);
}

function isImageByName(name: string): boolean {
  return IMAGE_EXTENSIONS.includes(getFileExtension(name));
}

function isImageFile(file: File): boolean {
  return file.type.startsWith('image/') || isImageByName(file.name);
}

export default ({
  label,
  name,
  value,
  setFieldValue,
  isSubmitting,
  touched,
  errors,
  accept,
  existingUrl,
  existingFileLabel,
  onRemoveExisting,
  disabled,
  maxCsvPreviewLines = 6,
  maxSizeMb = 2,
}: DocumentFileInputProps): JSX.Element => {
  const { t } = useTranslation();
  const [csvPreview, setCsvPreview] = React.useState<string>('');
  const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
  const [sizeError, setSizeError] = React.useState<string | null>(null);

  const maxBytes = maxSizeMb * 1024 * 1024;

  React.useEffect(() => {
    if (!value || isCsvFile(value)) {
      setCsvPreview('');
    }

    if (value && isImageFile(value)) {
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }

    setPreviewUrl(null);
    return undefined;
  }, [value]);

  React.useEffect(() => {
    if (!value || !isCsvFile(value)) {
      setCsvPreview('');
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      const lines = text.split('\n').slice(0, maxCsvPreviewLines).join('\n');
      setCsvPreview(lines);
    };
    reader.readAsText(value);
    return () => reader.abort();
  }, [value, maxCsvPreviewLines]);

  const existingFileName = existingUrl ? getFileNameFromUrl(existingUrl) : '';

  const showSelected = Boolean(value);
  const showExisting = !showSelected && Boolean(existingUrl) && !disabled;
  const showExistingReadOnly = !showSelected && Boolean(existingUrl) && disabled;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event?.currentTarget?.files;
    const file = files && files[0] ? files[0] : null;

    if (!file) return;

    if (file.size > maxBytes) {
      setSizeError(t('file-size-exceeded', { size: maxSizeMb }));
      return;
    }

    setSizeError(null);
    setFieldValue(name, file);
  };

  const existingIsImage = existingFileName ? isImageByName(existingFileName) : false;

  return (
    <div>
      {label && (
        <Typography variant="body2" style={{ marginBottom: 8 }}>
          {label}
        </Typography>
      )}

      {showSelected && value && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #ccc',
            borderRadius: 4,
            padding: '4px 8px',
            marginBottom: 8,
          }}
        >
          <Typography noWrap style={{ maxWidth: '70%' }}>
            {value.name}
          </Typography>
          <IconButton
            size="small"
            disabled={isSubmitting}
            onClick={() => setFieldValue(name, null)}
            aria-label={t('remove-file')}
          >
            <Close fontSize="small" />
          </IconButton>
        </div>
      )}

      {showSelected && value && isImageFile(value) && previewUrl && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            border: '1px solid #eee',
            borderRadius: 4,
            padding: 8,
            marginBottom: 8,
            background: '#fafafa',
          }}
        >
          <img src={previewUrl} alt={value.name} style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }} />
        </div>
      )}

      {showExisting && existingUrl && existingFileName && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            border: '1px solid #ccc',
            borderRadius: 4,
            padding: '4px 8px',
            marginBottom: 8,
          }}
        >
          <Typography noWrap style={{ maxWidth: '60%' }}>
            {existingFileLabel ? `${existingFileLabel}: ` : ''}
            {existingFileName}
          </Typography>
          <span>
            <Button
              size="small"
              component="a"
              href={existingUrl}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<GetApp />}
              disabled={isSubmitting}
            >
              {t('download-file')}
            </Button>
            {onRemoveExisting && (
              <IconButton size="small" disabled={isSubmitting} onClick={onRemoveExisting} aria-label={t('remove-file')}>
                <Close fontSize="small" />
              </IconButton>
            )}
          </span>
        </div>
      )}

      {showExisting && existingUrl && existingIsImage && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            border: '1px solid #eee',
            borderRadius: 4,
            padding: 8,
            marginBottom: 8,
            background: '#fafafa',
          }}
        >
          <img
            src={existingUrl}
            alt={existingFileName}
            style={{ maxWidth: '100%', maxHeight: 200, objectFit: 'contain' }}
          />
        </div>
      )}

      {showExistingReadOnly && existingUrl && existingFileName && (
        <Button
          size="small"
          component="a"
          href={existingUrl}
          target="_blank"
          rel="noopener noreferrer"
          startIcon={<GetApp />}
        >
          {existingFileLabel ? `${existingFileLabel}: ` : ''}
          {existingFileName}
        </Button>
      )}

      {!disabled && (
        <Button fullWidth variant="contained" color="primary" component="label" disabled={isSubmitting}>
          {showExisting || showSelected ? t('file-replace') : label}
          <input id={name} name={name} type="file" hidden accept={accept} onChange={handleFileChange} />
        </Button>
      )}

      {!disabled && (
        <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 4 }}>
          {t('file-max-size', { size: maxSizeMb })}
        </Typography>
      )}

      {csvPreview && (
        <div
          style={{
            marginTop: 8,
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '4px 8px',
              background: '#f5f5f5',
              borderBottom: '1px solid #e0e0e0',
            }}
          >
            <Chip size="small" color="primary" variant="outlined" label={t('file-csv-preview')} />
            <Typography variant="caption" color="textSecondary">
              {t('file-preview-lines', { count: maxCsvPreviewLines })}
            </Typography>
          </div>
          <pre
            style={{
              margin: 0,
              padding: 8,
              fontSize: 12,
              overflow: 'auto',
              maxHeight: 160,
              whiteSpace: 'pre',
              background: '#fff',
            }}
          >
            {csvPreview}
          </pre>
        </div>
      )}

      {(sizeError || (touched && errors)) && (
        <FormHelperText variant="outlined" error>
          {sizeError || errors}
        </FormHelperText>
      )}
    </div>
  );
};
